import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getOrganisations, updateOrganisation } from '../api';
import EmptyState from '../components/EmptyState';

function EditForm({ org, onSave, onCancel }) {
  const [name, setName] = useState(org.name);
  const [slug, setSlug] = useState(org.slug);
  const [address, setAddress] = useState(org.address || '');
  const [telephone, setTelephone] = useState(org.telephone || '');
  const [website, setWebsite] = useState(org.website || '');
  const [contactEmail, setContactEmail] = useState(org.contactEmail || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const updated = await updateOrganisation(org.id, { name, slug, address, telephone, website, contactEmail, isActive: org.isActive });
      onSave(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', marginTop: '0.5rem' }}>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="form-row">
        <div className="form-group">
          <label>Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="form-group">
          <label>Slug</label>
          <input type="text" value={slug} onChange={(e) => setSlug(e.target.value)} required />
        </div>
      </div>
      <div className="form-group">
        <label>Address</label>
        <textarea rows={3} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="e.g. Company Name&#10;Street&#10;City Postcode" />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Telephone</label>
          <input type="text" value={telephone} onChange={(e) => setTelephone(e.target.value)} placeholder="e.g. 0161 877 0586" />
        </div>
        <div className="form-group">
          <label>Website</label>
          <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="e.g. www.example.com" />
        </div>
        <div className="form-group">
          <label>Contact Email</label>
          <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="e.g. info@example.com" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button type="button" className="btn btn-outline" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

export default function OrganisationsPage() {
  const { isAdminForOrg, isAnyAdmin, isSystemAdmin } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    getOrganisations()
      .then(setOrgs)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = (updated) => {
    setOrgs((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
    setEditingId(null);
  };

  return (
    <div className="page-container">
      <div className="form-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2>Organisations</h2>
          {isSystemAdmin && <Link to="/add-organisation" className="btn btn-primary">Add Organisation</Link>}
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {loading && <p>Loading...</p>}

        {!loading && orgs.length === 0 && !error && <EmptyState message="No organisations found." />}

        {!loading && orgs.length > 0 && (
          <div className="table-scroll">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #e5e7eb' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #e5e7eb' }}>Slug</th>
                <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #e5e7eb' }}>Docs</th>
                {isAnyAdmin && <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #e5e7eb' }}>Users</th>}
                {isAnyAdmin && <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #e5e7eb' }}></th>}
              </tr>
            </thead>
            <tbody>
              {orgs.map((o) => (
                <tr key={o.id}>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }} colSpan={editingId === o.id ? (isAnyAdmin ? 5 : 3) : 1}>
                    {editingId === o.id ? (
                      <EditForm org={o} onSave={handleSave} onCancel={() => setEditingId(null)} />
                    ) : (
                      o.name
                    )}
                  </td>
                  {editingId !== o.id && (
                    <>
                      <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{o.slug}</td>
                      <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                        <Link to={`/org-documents?orgId=${o.id}`}>View</Link>
                      </td>
                      {isAnyAdmin && (
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                          {isAdminForOrg(o.id) && (
                            <Link to={`/organisation-users?orgId=${o.id}`}>Manage</Link>
                          )}
                        </td>
                      )}
                      {isAnyAdmin && (
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                          {isAdminForOrg(o.id) && (
                            <button className="btn btn-outline" style={{ fontSize: '0.8rem' }} onClick={() => setEditingId(o.id)}>Edit</button>
                          )}
                        </td>
                      )}
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
